import defaultLoading from '@/images/loading.png';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { t } from 'i18next';
import { Toast } from 'rc-ui-lib';
import type { Result } from '../../../types/response';
import type { ErrorType } from '../../api/interface/vertify';
import store from '../../store';
import { nativeApi } from '../nativeApi';
import axiosHttp, { createAxiosHttp } from './originAxios';
import { tryHandleUnauthorized } from './unauthorizedHandler';

export type Options = {
  hideError?: boolean;
  loading?: boolean;
  instance?: boolean;
  instanceConfig?: AxiosRequestConfig;
  isIntercept?: boolean;
};

type HttpFn = {
  <T>(url: string, params?: any, options?: Options, config?: any): Promise<T>;
};

export type Methods = 'get' | 'post' | 'delete' | 'put';
type Http = Record<Methods, HttpFn>;
const methods = ['get', 'post', 'delete', 'put'];

const defaultOptions: Options = {
  hideError: false, // 隐藏错误，默认内部展示错误信息
  loading: false, // 是否展示loading
  instance: false, // 是否需要实例，每次请求都返回一个新的实例
  instanceConfig: {}, // 实例配置
  isIntercept: true, // 返回token失效时，是否弹出登录界面
};
let requestCount = 0;
// byClaude-start 2026-08-05 19:45:00
// 标记 loading toast 是否已展示，避免重复调用 Toast.loading 造成微闪烁
let isLoadingShowing = false;
// byClaude-end
// byClaude-start 2026-08-05 17:45:00
// 延迟清除定时器：避免连续请求之间短暂归零导致loading闪烁
let clearTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleClear() {
  cancelClear();
  clearTimer = setTimeout(() => {
    if (requestCount === 0 && holdCount === 0) {
      Toast.clear();
      isLoadingShowing = false;
    }
    clearTimer = null;
  }, 500);
}

function cancelClear() {
  if (clearTimer) {
    clearTimeout(clearTimer);
    clearTimer = null;
  }
}

// 外部 hold 機制：供頁面級別防止 loading 在初始化期間被清除
// 僅在被調用時生效，不影響其他頁面
let holdCount = 0;

export function holdLoading() {
  holdCount++;
}

export function releaseLoading() {
  if (holdCount > 0) {
    holdCount--;
  }
  if (requestCount === 0 && holdCount === 0) {
    scheduleClear();
  }
}
// byClaude-end
// const requestTokenNum = 0;
// const isRefreshing = false; // 是否正在更新token
// const requestList: any = []; // 存放请求列表

const request: Http = methods.reduce((map: any, method: string) => {
  map[method] = (url: string, params?: any, options?: Options, config?: any) => {
    const optionsData = { ...defaultOptions, ...(options || {}) };
    const { instance, hideError, loading, instanceConfig, isIntercept = true } = optionsData;
    if (loading) {
      // byClaude-start 2026-08-05 12:37:26
      // 有新请求进入时取消待执行的清除定时器，保持loading连续显示
      cancelClear();
      // byClaude-end
      // byClaude-start 2026-08-05 19:45:00
      if (!isLoadingShowing) {
        Toast.loading({
          icon: <img src={defaultLoading} alt="defaultLoading" className="default-loading-icon" />,
          className: 'default-loading',
          forbidClick: true,
          duration: 0,
        });
        isLoadingShowing = true;
      }
      // byClaude-end
      requestCount++;
    }
    return new Promise((resolve, reject) => {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      handleRequest({
        instance: instance as boolean,
        instanceConfig: instanceConfig as AxiosRequestConfig,
        method: method as Methods,
        url,
        params,
        config,
        resolve,
        reject,
        hideError: hideError as boolean,
        loading: loading as boolean,
        isIntercept,
      });
    });
  };
  return map;
}, {});

export type HandleRequestParams = {
  instance: boolean;
  instanceConfig: AxiosRequestConfig;
  method: Methods;
  url: string;
  params: any;
  config: any;
  resolve: { (value: unknown): void; (arg0: AxiosResponse<Result<any>, any>): void };
  reject: { (reason?: any): void; (arg0: { message: string; code: string }): void };
  hideError: boolean;
  loading: boolean;
  isIntercept: boolean; // token失效是否拦截
  needGoOnafterIntercept?: boolean; // 在拦截之后是否继续执行，默认为否
};

const handleRequest = (requestparams: HandleRequestParams) => {
  const {
    instance,
    instanceConfig,
    method,
    url,
    params,
    config,
    resolve,
    reject,
    hideError,
    loading,
    isIntercept = true,
  } = requestparams;
  (instance ? createAxiosHttp(instanceConfig) : axiosHttp)
    [method as Methods](url, params, config)
    .then(
      (res: AxiosResponse<Result>) => {
        if (loading) {
          requestCount--;
          // byClaude-start 2026-08-05 12:37:26
          if (requestCount === 0) {
            scheduleClear();
          }
          // byClaude-end
        }

        setTimeout(() => {
          resolve(res);
        }, 0);
      },
      (e: ErrorType) => {
        console.log('code', e.code, e, loading, requestCount, hideError);
        if (loading) {
          requestCount--;
          // byClaude-start 2026-08-05 12:37:26
          if (requestCount === 0) {
            scheduleClear();
          }
          // byClaude-end
        }
        // token过期
        if (
          e.code &&
          (Number(e.code) === 101 || Number(e.code) === 102 || Number(e.code) === 100)
        ) {
          console.log(
            e.code,
            `Bearer ${store.getState().commonReducer.userToken}`,
            '进入了登录页面了！！！！！token'
          );
          if (tryHandleUnauthorized(e)) {
            reject(e);
            return;
          }

          if (isIntercept) {
            const retFromReLogin = nativeApi.reLogin?.();
            if (retFromReLogin instanceof Promise) {
              retFromReLogin.then((data) => {
                nativeApi.setToken?.(data);
                if (data) {
                  // 重新登录成功
                  const intro = {
                    needGoon: true,
                  };
                  reject(intro);
                } else {
                  // 重新登录失败
                  reject(e);
                }
              });
            }
          } else {
            reject(e);
          }
        } else if (e.code && Number(e.code) === 607) {
          setTimeout(() => {
            window.showGlobalDialog?.({
              title: t<string>('ekyc_hint_title'),
              content: t<string>('ekyc_hint_content'),
              confirmButtonColor: '#00C34E',
              showCancelButton: false,
              confirmButtonText: t<string>('確認'),
            });
          }, 0);
          reject(e);
        } else {
          // 通用报错的处理
          if (!hideError && e && e.message && !e.message.includes('timeout')) {
            setTimeout(() => {
              Toast.info({
                message: e.message,
              });
            }, 0);
          }
          reject(e);
        }
      }
    );
};

export default request;

// Contains claude-generated edits.
